import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowLeft, Crown, Medal, Award, Sparkles } from "lucide-react";
import api from "../lib/api";
import { fmt } from "../lib/money";
import { useAuth } from "../context/AuthContext";

const PODIUM_COLORS = {
  1: { bg: "bg-[#FFE600]", text: "text-black", border: "border-black", icon: Crown, label: "Champion" },
  2: { bg: "bg-neutral-200", text: "text-black", border: "border-black", icon: Medal, label: "Runner-up" },
  3: { bg: "bg-[#FF9500]", text: "text-white", border: "border-black", icon: Award, label: "Third place" },
};

export default function AffiliateLeaderboard() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/affiliate/leaderboard?period=${period}&limit=20`)
      .then(({ data }) => { if (alive) { setData(data); setLoading(false); } })
      .catch(() => { if (alive) { setData({ rows: [] }); setLoading(false); } });
    return () => { alive = false; };
  }, [period]);

  // My rank — only fetched for signed-in users
  useEffect(() => {
    if (!user) { setMyRank(null); return; }
    let alive = true;
    api.get(`/affiliate/my-rank?period=${period}`)
      .then(({ data }) => { if (alive) setMyRank(data); })
      .catch(() => { if (alive) setMyRank(null); });
    return () => { alive = false; };
  }, [user, period]);

  const monthName = new Date().toLocaleString("en", { month: "long" });
  const rows = data?.rows || [];
  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-14">
      <Link to="/" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-neutral-500 hover:text-black mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to marketplace
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <Trophy className="w-8 h-8 text-[#FFE600] fill-[#FFE600] stroke-black" />
        <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tight">Affiliate leaderboard</h1>
      </div>
      <p className="text-sm text-neutral-600 max-w-xl">
        The top shoppers turning shares into commissions on Guyana Discounts. Want to join the board? Share any product link from your account.
      </p>

      {/* Period tabs */}
      <div className="mt-6 inline-flex border-2 border-black">
        {[
          { k: "month", l: `This month · ${monthName}` },
          { k: "all", l: "All time" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`lb-period-${t.k}`}
            onClick={() => setPeriod(t.k)}
            className={`h-10 px-4 text-xs uppercase tracking-widest font-bold transition-colors ${
              period === t.k ? "bg-black text-[#FFE600]" : "bg-white text-black hover:bg-neutral-100"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {/* Your rank — signed-in users only */}
      {myRank && (
        <div
          data-testid="lb-my-rank"
          className="mt-6 border-2 border-[#0033FF] bg-[#0033FF] text-white p-4 sm:p-5 flex items-center gap-4 flex-wrap"
        >
          <Sparkles className="w-6 h-6 shrink-0" />
          <div className="min-w-0 flex-1">
            {myRank.rank ? (
              <>
                <div className="font-display font-black text-2xl sm:text-3xl leading-tight">
                  You're <span className="text-[#FFE600]">#{myRank.rank}</span>
                  <span className="text-[14px] sm:text-base font-normal opacity-80 ml-2">of {myRank.total}</span>
                </div>
                <div className="text-sm mt-1 opacity-90 flex flex-wrap gap-x-4 gap-y-0.5">
                  <span><b>{fmt(myRank.earned)}</b> earned</span>
                  <span>{myRank.conversions} {myRank.conversions === 1 ? "sale" : "sales"}</span>
                  {myRank.gap_to_next != null && myRank.gap_to_next > 0 && myRank.next_name && (
                    <span className="text-[#FFE600] font-bold">
                      +{fmt(myRank.gap_to_next)} more overtakes #{myRank.rank - 1} ({myRank.next_name})
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="font-display font-black text-2xl leading-tight">
                  You haven't earned yet this {period === "month" ? "month" : "period"}
                </div>
                <div className="text-sm mt-1 opacity-90">
                  Be the first sale on your account — share any product link and your name lands on the board.
                </div>
              </>
            )}
          </div>
          <Link
            to="/affiliate"
            data-testid="lb-my-rank-cta"
            className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3 bg-[#FFE600] text-black text-[11px] uppercase tracking-widest font-bold hover:bg-white transition-colors"
          >
            Grab share link →
          </Link>
        </div>
      )}

      {loading ? (
        <div className="mt-10 text-sm text-neutral-500">Loading leaderboard…</div>
      ) : rows.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-neutral-300 p-10 text-center">
          <Trophy className="w-12 h-12 mx-auto text-neutral-300 mb-3" />
          <p className="font-display font-black text-xl mb-2">No commissions yet this {period === "month" ? "month" : "period"}</p>
          <p className="text-sm text-neutral-500 max-w-md mx-auto">Be the first on the board — share a product link from your account and earn commission on every sale.</p>
          <Link to="/affiliate" className="mt-4 inline-flex items-center gap-1.5 bg-[#0033FF] hover:bg-[#0022CC] text-white h-10 px-4 text-xs uppercase tracking-widest font-bold">
            Get your share link →
          </Link>
        </div>
      ) : (
        <>
          {/* Podium — top 3 */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="lb-podium">
            {podium.map((p) => {
              const cfg = PODIUM_COLORS[p.rank];
              const Icon = cfg.icon;
              return (
                <div
                  key={p.slug}
                  data-testid={`lb-podium-${p.rank}`}
                  className={`border-2 ${cfg.border} ${cfg.bg} ${cfg.text} p-5 sm:p-6 relative ${p.rank === 1 ? "sm:scale-[1.03] sm:z-10 shadow-[8px_8px_0_0_#000]" : "shadow-[4px_4px_0_0_#000]"}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">{cfg.label}</div>
                      <div className="font-display font-black text-5xl leading-none mt-1">#{p.rank}</div>
                    </div>
                    <Icon className="w-8 h-8 shrink-0 opacity-90" />
                  </div>
                  <div className="font-display font-black text-2xl sm:text-3xl leading-tight">{p.display_name}</div>
                  <div className="text-[11px] opacity-70 font-mono">@{p.slug}</div>
                  <div className="mt-3 font-display font-black text-3xl sm:text-4xl">
                    {fmt(p.earned)}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest font-bold opacity-80 mt-0.5">
                    earned · {p.conversions} {p.conversions === 1 ? "sale" : "sales"}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Rest of the table (rank 4+) */}
          {rest.length > 0 && (
            <div className="mt-10 border-2 border-black bg-white" data-testid="lb-rest">
              <div className="bg-black text-[#FFE600] px-4 py-2 text-[11px] uppercase tracking-widest font-bold">
                The rest of the board
              </div>
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold w-16">Rank</th>
                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Shopper</th>
                    <th className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Sales</th>
                    <th className="text-right px-4 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Earned</th>
                  </tr>
                </thead>
                <tbody>
                  {rest.map((p) => (
                    <tr key={p.slug} data-testid={`lb-row-${p.rank}`} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50">
                      <td className="px-4 py-3 font-display font-black text-lg">#{p.rank}</td>
                      <td className="px-4 py-3">
                        <div className="font-bold">{p.display_name}</div>
                        <div className="text-[10px] text-neutral-500 font-mono">@{p.slug}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-neutral-600">{p.conversions}</td>
                      <td className="px-4 py-3 text-right font-display font-black">{fmt(p.earned)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="mt-10 text-[11px] text-neutral-500 text-center">
        Updated in real time · Names are shown as first-name + last-initial for privacy · Commission counts when an order is marked <b>delivered</b>.
      </div>
    </div>
  );
}
